import type { InsightTopologyResponse } from "../../types/insightApi";

/**
 * Turns an empty topology response into the title + message the user reads
 * (CTX-422).
 *
 * Before CTX-409 every empty result arrived as `200 / "ok"`, so the canvas
 * could only ever say "No topology for this scope" — true when the scope is
 * genuinely empty, and a lie when nothing was read at all. `state` now
 * carries the cause, and this is the single place that turns it into words.
 *
 * **Why the wording differs from the `no-processed-scope` state in
 * `InsightTopologyViewV2` (CTX-424):** they are different claims, and saying
 * the same thing twice would make one of them wrong. CTX-424 speaks when
 * `listTopologyScopes` returned *nothing at all*, so it can honestly say the
 * source has no data for any scope. `not_processed` here is narrower — the
 * request came back empty for **this** scope, and production showed exactly
 * the case where the same source holds 112 nodes under a different account
 * (CTX-2.1-TEST). Claiming "this source has not been processed" would be
 * false there.
 */
export interface EmptyStateCopy {
	title: string;
	description: string;
}

export const DEFAULT_EMPTY_STATE: EmptyStateCopy = {
	title: "No topology for this scope",
	description: "This source has no digested topology for the selected cloud/region/account yet.",
};

/** `cloud / region / account`, skipping whatever the response did not carry. */
function formatRequestedScope(topology: InsightTopologyResponse): string {
	const parts = [topology.cloud, topology.region, topology.account].filter(Boolean);
	return parts.length > 0 ? parts.join(" / ") : "the selected scope";
}

/**
 * Reason-derived description for the CTX-97 "stale partial banner" case,
 * where members were read but none produced topology. Kept ahead of the
 * generic fallback and behind the `state` cases: a named member reason is
 * more specific than "nothing came back".
 */
function describeUnavailableMembers(topology: InsightTopologyResponse): string | undefined {
	const members = topology.unavailableMembers;
	if (!members || members.length === 0) return undefined;
	const reasons = new Set(members.map((member) => member.reason));
	if (reasons.has("missing")) return "No digest has been processed yet for this source.";
	if (reasons.has("failed")) return "The latest processing run failed for this source.";
	if (reasons.has("deleted")) return "This source's discovery data has been deleted.";
	if (reasons.has("unavailable") || reasons.has("timeout")) {
		return "The Insight processing service is temporarily unavailable for this source.";
	}
	return undefined;
}

/**
 * An unrecognised `state` falls through to the default empty copy rather
 * than rendering blank — the contract is additive, so a value this build has
 * never heard of must degrade, never break.
 */
export function describeEmptyState(topology: InsightTopologyResponse | null | undefined): EmptyStateCopy {
	if (!topology) return DEFAULT_EMPTY_STATE;

	if (topology.state === "topology_unavailable") {
		return {
			title: "Topology not available",
			description: `Discovery completed for ${formatRequestedScope(topology)}, but no topology map came with it. Try again in a few minutes — if it keeps happening, contact support.`,
		};
	}

	if (topology.state === "not_processed") {
		return {
			title: "No data for this scope",
			description: `Nothing has been processed for ${formatRequestedScope(topology)}. This source may still hold data for a different cloud, region or account — pick another scope from the source browser.`,
		};
	}

	const memberDescription = describeUnavailableMembers(topology);
	return memberDescription ? { ...DEFAULT_EMPTY_STATE, description: memberDescription } : DEFAULT_EMPTY_STATE;
}
